import { loading, toastMessage } from "src/store/store";
import client from "../client";
import { convertDateOnly } from "../helpers";
import { extractErrorMessage } from "src/utils/misc";
import { SubmitInvestigationData } from "src/utils/interface";

export async function getJobDetails(id) {
	try {
		loading.set(true);
		const response = await client(`/v1/jobs/${id}`, {
			method: "GET",
			version: "Tasking",
		});
		return response;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	} finally {
		loading.set(false);
	}
}

export async function patchCaseStatus(
	id: string,
	data: { status: string; reason?: string }
) {
	try {
		const response = await client(`/v1/CaseManagement/case/${id}/status`, {
			method: "PATCH",
			version: "Tasking",
			data,
		});
		return response;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		return;
	}
}

export async function createCaseFromDesmond(data: any) {
	try {
		const res = await client("/v1/CaseManagement/case", {
			method: "PUT",
			version: "Tasking",
			data: {
				cases: data,
			},
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function createManualCase(formData: FormData) {
	try {
		const res = await client("/v1/CaseManagement/case/manual", {
			method: "POST",
			version: "Tasking",
			data: formData,
			header: "multipart",
		});
		return res;
	} catch (error) {
		console.error(error);
		const errCode = error.response?.status;
		const errors = error.response?.data;
		if (errCode === 400 && errors) {
			extractErrorMessage(Object.values(errors).flat());
		} else {
			extractErrorMessage(error);
		}
		throw error;
	}
}

export async function getCaseDetails(id: string) {
	try {
		const res = await client(`/v1/CaseManagement/case/${id}`, {
			method: "GET",
			version: "Tasking",
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function createQuotationOrEditCase(
	id: string,
	formData: FormData
) {
	try {
		const res = await client(`/v1/CaseManagement/case/${id}/quotation`, {
			method: "PUT",
			version: "Tasking",
			data: formData,
			header: "multipart",
		});
		return res;
	} catch (error) {
		console.error(error);
		const errCode = error.response?.status;
		const errors = error.response?.data;
		if (errCode === 400 && errors) {
			extractErrorMessage(Object.values(errors).flat());
		} else {
			extractErrorMessage(error);
		}
		throw error;
	}
}

export async function getReportDetails(
	id: string,
	{
		bookingId,
		vehicleId,
		type,
	}: { bookingId?: string; vehicleId: string; type?: string }
) {
	try {
		const res = await client(
			`/v1/CaseManagement/case/${id}/report/details?bookingId=${bookingId}&vehicleId=${vehicleId}${
				type ? `&type=${type}` : ""
			}`,
			{ method: "GET", version: "Tasking" }
		);
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getCaseReports(
	id: string,
	vehicleId: string,
	lastEvaluatedKey?: string
) {
	try {
		if (lastEvaluatedKey) {
			vehicleId = vehicleId + `&lastEvaluatedKey=${lastEvaluatedKey}`;
		}
		const res = await client(
			`/v1/CaseManagement/case/${id}/reports?vehicleId=${vehicleId}`,
			{ method: "GET", version: "Tasking" }
		);
		return res;
	} catch (error) {
		console.error(error);
		if (error.response?.status === 404 && error.response.data) {
			extractErrorMessage(error.response.data);
		} else {
			extractErrorMessage(error);
		}
		throw error;
	}
}

export async function getCaseActivities(caseNo: string, size?: number) {
	try {
		const res = await client(
			`/v1/CaseManagement/case/actionLog?Query=${caseNo}${
				size ? `&size=${size}` : ""
			}`,
			{
				method: "GET",
				version: "Tasking",
			}
		);
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function exportCaseByCaseNo(caseNo: [string]) {
	try {
		const res = await client(`/v1/CaseManagement/case/export`, {
			method: "POST",
			version: "Tasking",
			data: { caseNo: caseNo },
		});
		const url = window.URL.createObjectURL(new Blob([res.data]));
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "PreBillingCases.csv"); //or any other extension
		document.body.appendChild(link);
		link.click();
		toastMessage.set({
			type: "success",
			message: "CSV successfully exported",
		});
		return;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function videoUpload(files: File) {
	// Implement the logic to upload files to 1GB here
	try {
		console.log("Uploading files to 1GB:", files);
		const file = files;
		if (!file) return alert("Select a file first");

		// Step 1: Initiate upload
		const initRes = await client(
			`/v1/S3Upload/initiate?fileName=${encodeURIComponent(file.name)}`,
			{
				method: "POST",
				version: "Tasking",
			}
		);

		let initChunkSize = 10 * 1024 * 1024; // 10 MB

		const { key, uploadId, chunkSize } = initRes.data;

		if (chunkSize) {
			initChunkSize = chunkSize;
		}

		const totalParts = Math.ceil(file.size / initChunkSize);
		const partETags = [];

		for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
			const start = (partNumber - 1) * initChunkSize;
			const end = Math.min(partNumber * initChunkSize, file.size);
			const blob = file.slice(start, end);

			// Get pre-signed URL for this part
			const presignRes = await client(
				`/v1/S3Upload/part-url?key=${encodeURIComponent(
					key
				)}&uploadId=${uploadId}&partNumber=${partNumber}`,
				{
					version: "Tasking",
					method: "GET",
				}
			);
			const { url } = await presignRes.data;

			// Upload chunk
			const uploadRes = await fetch(url, {
				method: "PUT",
				body: blob,
				headers: { "Access-Control-Allow-Origin": "*" },
			});

			if (!uploadRes.ok) throw new Error(`Part ${partNumber} failed`);

			const eTag = uploadRes.headers.get("ETag").replace(/"/g, "");
			partETags.push({ PartNumber: partNumber, ETag: eTag });
		}

		const completeRes = await client("/v1/S3Upload/complete", {
			method: "POST",
			version: "Tasking",
			headers: { "Content-Type": "application/json" },
			data: JSON.stringify({
				Key: key,
				UploadId: uploadId,
				PartETags: partETags,
			}),
		});

		return completeRes;
	} catch (error) {
		console.error("Error uploading to 1GB:", error);
	}
}

export async function postVideoUrlToCase(caseId: string, videoUrl) {
	try {
		const res = await client(
			`/v1/CaseManagement/case/${caseId}/additionalPhotos`,
			{
				method: "PATCH",
				version: "Tasking",
				data: {
					urls: videoUrl,
				},
			}
		);
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function collateVideoUrls(fileArr: any[]) {
	try {
		const urlArr = [];
		for (const file of fileArr) {
			const uploadRes = await videoUpload(file);
			if (uploadRes && uploadRes.data && uploadRes.data.location) {
				const fileUrl = uploadRes.data.location;
				urlArr.push(fileUrl);
			} else {
				console.error("Upload response is invalid:", uploadRes);
			}
		}
		return urlArr;
	} catch (error) {
		console.error("Error collating video URLs:", error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function submitInvestigation(
	caseId: string,
	data: SubmitInvestigationData
) {
	try {
		const res = await client(
			`/v1/CaseManagement/case/${caseId}/submitInvestigation`,
			{
				method: "PUT",
				version: "Tasking",
				data,
			}
		);
		return res;
	} catch (error) {
		console.log(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function exportServicingCsv() {
	try {
		const res = await client("/v1/servicing/csvFileExport", {
			method: "POST",
			version: "Tasking",
		});
		if (res.status === 200) {
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute(
				"download",
				`servicing_${convertDateOnly(new Date(), "YYYYMMDD")}.csv`
			);
			document.body.appendChild(link);
			link.click();
			toastMessage.set({
				message: "Exported CSV file: All items",
				type: "success",
			});
		}
	} catch (error) {
		extractErrorMessage(error);
		throw error;
	}
}

export async function getMissingServicingRules() {
	try {
		const res = await client("/v1/servicing/rules/missing", {
			version: "Tasking",
			method: "GET",
		});
		return res;
	} catch (error) {
		console.error(error);
		extractErrorMessage(error);
		throw error;
	}
}

export async function getServicingList(params: {
	page: number;
	size: number | string;
	status: number;
	plateNumber?: string;
	sortCol?: string;
	sortDir?: string;
}) {
	const res = await client("/v1/servicing", {
		method: "GET",
		version: "Tasking",
		params,
	});
	return res;
}
